/**
 * Coop mid-pipeline snapshots often expose verdict `NO_ACTION` / `noop` while the
 * case is still open. Never surface the raw “NO ACTION” chip — it reads like
 * “awaiting work” and “finished with nothing to do” at the same time.
 * Pipeline Test and Content Management share these helpers.
 *
 * Prefixes:
 * - `Coop · …` — live Coop scan lifecycle / recorded enforcement (`formatCoopScanBadge`)
 * - `TipMi preview · …` — media-manager content-graph rule preview
 *   (`_evaluate_rules` / harness). Not a live Coop action execution — do not use
 *   on cards titled “Coop scan” / “Coop intelligence”.
 * - `TipMi · …` — TipMi publication ledger (via moderationStatusLabel)
 */

/** Mid-pipeline Coop labels that must not be treated as final outcomes. */
export const PROVISIONAL_CASE_STATUSES = new Set([
  'heuristic',
  'standard',
  'deep',
  'partial',
  'processing',
]);

/**
 * Statuses that are not real Coop decisions. Admin-api used to invent `completed`
 * whenever any vectors/signals existed; `proactive_publish` is a case_type, not a
 * lifecycle status. Neither may flip a NO_ACTION snapshot to “finished”.
 */
const WEAK_CASE_STATUSES = new Set(['completed', 'proactive_publish']);

const COOP_PREFIX = 'Coop · ';
const PREVIEW_PREFIX = 'TipMi preview · ';

function coopLabel(value: string): string {
  return `${COOP_PREFIX}${value}`;
}

function previewLabel(value: string): string {
  return `${PREVIEW_PREFIX}${value}`;
}

export function isNoActionLike(verdict: string | null | undefined): boolean {
  const normalized = verdict?.toLowerCase().replace(/[\s-]+/g, '_') ?? '';
  return (
    normalized === 'no_action' ||
    normalized === 'noop' ||
    normalized.includes('no_action') ||
    normalized.includes('no action')
  );
}

/** True terminal Coop / harness outcomes — safe to stop polling / show final label. */
export function isTerminalCaseStatus(raw: string | null | undefined): boolean {
  const status = (raw ?? '').toLowerCase();
  return (
    status === 'approved' ||
    status === 'rejected' ||
    status === 'escalated' ||
    status === 'escalated_human' ||
    status === 'escalated_gpu' ||
    // Legacy / admin-synthesized; keep for non-NO_ACTION polling stop.
    // For NO_ACTION, isProvisionalNoAction treats this as weak (still awaiting).
    status === 'completed' ||
    status === 'failed' ||
    status === 'timeout'
  );
}

/**
 * True once TipMi has recorded a finished deep pass (or delivered vectors show
 * analysis_complete). Distinguishes mid-pipeline NO_ACTION snapshots from a
 * finished Coop scan that never produced APPROVE/REJECT.
 */
export function isDeepScanSettled(opts: {
  deepScanDurationMs?: number | null | undefined;
  deliveryStatus?: string | null | undefined;
  vectors?: Record<string, Record<string, number>> | null | undefined;
}): boolean {
  if (opts.deepScanDurationMs != null && Number.isFinite(opts.deepScanDurationMs)) {
    return true;
  }
  const delivery = (opts.deliveryStatus ?? '').trim().toLowerCase();
  if (delivery !== 'delivered' && delivery !== 'success') return false;
  const complete = opts.vectors?.moderation?.analysis_complete;
  return complete != null && complete >= 1;
}

/**
 * True when Coop’s verdict is a provisional no-op snapshot (not a final decision).
 * Unknown/blank/`completed` case_status is treated as non-terminal so we prefer
 * “Coop · Scanning” until Coop reports a real decision status — unless the deep
 * scan has already settled (then NO_ACTION means “scan done, nothing to enforce”).
 */
export function isProvisionalNoAction(
  verdict: string | null | undefined,
  caseStatus: string | null | undefined,
  opts?: { deepScanSettled?: boolean | undefined } | undefined,
): boolean {
  if (!isNoActionLike(verdict)) return false;
  if (opts?.deepScanSettled) return false;
  if (!caseStatus) return true;
  const status = caseStatus.toLowerCase();
  if (WEAK_CASE_STATUSES.has(status)) return true;
  if (isTerminalCaseStatus(caseStatus)) return false;
  return true;
}

/** Map a raw Coop verdict code to the system-specific display word (no prefix). */
function coopVerdictWord(verdict: string): string {
  const upper = verdict.toUpperCase();
  if (upper === 'HUMAN_REVIEW' || upper.includes('HUMAN REVIEW') || upper.includes('HUMAN_REVIEW')) {
    return 'Escalated';
  }
  if (upper.includes('ESCALAT')) return 'Escalated';
  if (upper === 'BYPASSED' || upper.includes('BYPASS')) return 'Approved';
  if (upper.includes('APPROVE')) return 'Approved';
  if (upper.includes('REJECT')) return 'Rejected';
  if (upper.includes('PENDING')) return 'Pending';
  return verdict.replaceAll('_', ' ');
}

/**
 * Map a live Coop enforcement action (or action+verdict) to a Coop badge word.
 * Returns null when the payload is not an executable enforcement outcome.
 */
export function coopActionWord(actionOrVerdict: string | null | undefined): string | null {
  if (!actionOrVerdict) return null;
  const upper = actionOrVerdict.toUpperCase();
  if (
    upper.includes('AUTO_REJECT') ||
    upper.includes('REJECT') ||
    upper.includes('HIDE')
  ) {
    return 'Rejected';
  }
  if (
    upper.includes('AUTO_APPROVE') ||
    upper.includes('APPROVE') ||
    upper.includes('PUBLISH') ||
    upper.includes('BYPASS')
  ) {
    return 'Approved';
  }
  if (
    upper.includes('FRONTIER') ||
    upper.includes('ENQUEUE') ||
    upper.includes('MRT') ||
    upper.includes('HUMAN') ||
    upper.includes('ESCALAT') ||
    upper.includes('INVOKE')
  ) {
    return 'Escalated';
  }
  return null;
}

function badgeWordFromActionRows(
  rows: ReadonlyArray<{ action?: string | null; verdict?: string | null }>,
): string | null {
  if (rows.length === 0) return null;
  const last = rows[rows.length - 1];
  return (
    coopActionWord(last?.verdict) ??
    coopActionWord(last?.action) ??
    'Action taken'
  );
}

/**
 * Coop scan / Coop intelligence primary badge — live Coop only.
 * Never uses TipMi rule-preview (content-graph `_evaluate_rules`) as the main status.
 *
 * Prefer last `enforcement_actions`, then `enforcement_previews` (media-manager often
 * leaves actions empty while previews carry AUTO_REJECT), then live case `verdict`,
 * otherwise lifecycle (Scanning / Failed / No action).
 */
export function formatCoopScanBadge(opts: {
  caseStatus?: string | null | undefined;
  deepScanSettled?: boolean | undefined;
  enforcementActions?: ReadonlyArray<{ action?: string | null; verdict?: string | null }> | null | undefined;
  /** Planned Coop enforcement when `enforcement_actions` was not mirrored (e.g. content/5508). */
  enforcementPreviews?: ReadonlyArray<{ action?: string | null; verdict?: string | null }> | null | undefined;
  /** Live Coop case verdict — used when action rows are absent. */
  verdict?: string | null | undefined;
}): string {
  const status = (opts.caseStatus ?? '').toLowerCase();
  if (status === 'failed') return coopLabel('Failed');
  if (status === 'timeout') return coopLabel('Timed out');
  if (status === 'awaiting_delivery') return coopLabel('Opening');

  // Mid-pipeline NO_ACTION: never claim Approved/Rejected from enforcement rows or
  // previews — overview strip and Moderation History treat this as Scanning.
  if (
    isProvisionalNoAction(opts.verdict, opts.caseStatus, {
      deepScanSettled: opts.deepScanSettled,
    })
  ) {
    return coopLabel('Scanning');
  }

  // Mid-flight ESCALATE/APPROVE/REJECT from premature rules (e.g. human_last_resort on
  // analysis_incomplete) must stay Scanning until deep settles or Coop case_status is
  // a strong terminal. Failed/timeout already returned above.
  const strongTerminal =
    status === 'approved' ||
    status === 'rejected' ||
    status === 'escalated' ||
    status === 'escalated_human' ||
    status === 'escalated_gpu';
  if (!opts.deepScanSettled && !strongTerminal) {
    return coopLabel('Scanning');
  }

  const fromActions = badgeWordFromActionRows(opts.enforcementActions ?? []);
  if (fromActions) return coopLabel(fromActions);

  const fromPreviews = badgeWordFromActionRows(opts.enforcementPreviews ?? []);
  if (fromPreviews) return coopLabel(fromPreviews);

  // Case decided REJECT/APPROVE/ESCALATE but mirror omitted both action collections.
  if (opts.verdict && !isNoActionLike(opts.verdict)) {
    const fromVerdict =
      coopActionWord(opts.verdict) ?? coopVerdictWord(opts.verdict);
    if (
      fromVerdict === 'Rejected' ||
      fromVerdict === 'Approved' ||
      fromVerdict === 'Escalated' ||
      fromVerdict === 'Pending'
    ) {
      return coopLabel(fromVerdict);
    }
  }

  if (!opts.deepScanSettled) {
    if (isTerminalCaseStatus(opts.caseStatus) && !WEAK_CASE_STATUSES.has(status)) {
      // Real Coop terminal statuses without mirrored actions (rare).
      if (status.includes('reject')) return coopLabel('Rejected');
      if (status.includes('approv')) return coopLabel('Approved');
      if (status.includes('escalat')) return coopLabel('Escalated');
    }
    return coopLabel('Scanning');
  }

  // Deep finished; Coop did not record an enforcement action on this mirror.
  return coopLabel('No action');
}

/**
 * TipMi content-graph rule preview chip (not for cards titled Coop).
 * Lifecycle failures still use `Coop ·` because those are scan delivery outcomes.
 */
export function formatVerdictLabel(
  verdict: string | null | undefined,
  caseStatus?: string | null,
  opts?: {
    deepScanSettled?: boolean | undefined;
    tipmiLedgerPending?: boolean | undefined;
  } | undefined,
): string {
  const status = (caseStatus ?? '').toLowerCase();
  // Hard scan failures must win over NO_ACTION → "No action" (video Mux/deep scan).
  if (status === 'failed') return coopLabel('Failed');
  if (status === 'timeout') return coopLabel('Timed out');
  // TipMi outbox queued / retrying before media-manager mirror exists.
  if (status === 'awaiting_delivery') return coopLabel('Opening');
  if (!verdict) return coopLabel('Pending');
  if (isProvisionalNoAction(verdict, caseStatus, opts)) return coopLabel('Scanning');
  if (isNoActionLike(verdict)) {
    // Deep finished with nothing to enforce, but TipMi publication never decided
    // (e.g. enforcement off / risk above clear-band). Do not keep saying Scanning.
    if (opts?.deepScanSettled && opts?.tipmiLedgerPending) {
      return previewLabel('No action (TipMi decide pending)');
    }
    return previewLabel('No action');
  }
  return previewLabel(coopVerdictWord(verdict));
}

/**
 * Pipeline Test verdict chip. Mid-pipeline NO_ACTION → “Coop · Scanning”;
 * terminal outcomes → “TipMi preview · …” (local harness, not Coop actions).
 */
export function displayHarnessVerdictLabel(verdict: string | undefined, isFinal: boolean): string {
  if (!verdict) return 'No verdict returned';
  if (isNoActionLike(verdict)) {
    return isFinal ? previewLabel('No action') : coopLabel('Scanning');
  }
  if (isFinal) return previewLabel(coopVerdictWord(verdict));
  return previewLabel(`Snapshot · ${coopVerdictWord(verdict)}`);
}

/** Format an action/preview verdict chip without case_status (never raw NO_ACTION). */
export function formatActionVerdictLabel(verdict: string | null | undefined): string {
  if (!verdict) return '';
  if (isNoActionLike(verdict)) return previewLabel('No action');
  return previewLabel(coopVerdictWord(verdict));
}
